import { CHESSLA_PRODUCT_GUIDE, PRINCIPLE_IDS, type PrincipleId } from "@chessla/shared";
import { PATTERN_GLYPHS, type PatternGlyphKind } from "@/shared/components/PatternGlyph";
import type { POSITION_RELATIONSHIP_LEGEND } from "@/features/board";

const HELP_KICKERS = [
  "01 · Start here",
  "02 · Games",
  "03 · Analyze",
  "04 · Game review",
  "05 · Principles",
  "06 · Patterns",
  "07 · Trends",
  "08 · Nikola",
  "09 · Profile and privacy",
] as const;
export const HELP_SECTIONS = [
  ...CHESSLA_PRODUCT_GUIDE.map((section, index) => ({ ...section, kicker: HELP_KICKERS[index] })),
  {
    id: "glossary",
    title: "Chessla glossary",
    kicker: "10 · Reading the evidence",
    body: [
      "The short labels around Chessla carry exact meanings. Use this field guide whenever a review grade, principle, position value, or game format needs decoding.",
    ],
  },
];

export const HELP_SCREENSHOTS = [
  {
    section: "games",
    src: "/help/games-library.webp",
    alt: "Chessla Games library with scoped rows and review actions",
    caption: "Games · choose an owned game inside the right performance scope.",
    width: 1920,
    height: 1080,
  },
  {
    section: "analyze",
    src: "/help/analyze-workspace.webp",
    alt: "Chessla Analyze workspace showing analysis depth choices",
    caption: "Analyze · select work intentionally, then let Chessla finish and save.",
    width: 1920,
    height: 1080,
  },
  {
    section: "review",
    src: "/help/game-review.webp",
    alt: "Chessla Open Review with board, move timeline, evaluation and thinking times",
    caption: "Game Review · inspect recorded moves, evaluation, thinking time, and verified evidence in Open review.",
    width: 1920,
    height: 1080,
  },
  {
    section: "patterns",
    src: "/help/patterns-dossier.webp",
    alt: "Chessla Patterns dossier with recurring evidence",
    caption: "Patterns · recurrence stays format-specific and links back to exact games.",
    width: 1920,
    height: 1080,
  },
  {
    section: "trends",
    src: "/help/trends-evidence.webp",
    alt: "Chessla Trends charts with observed performance evidence",
    caption: "Trends · inspect values and uncertainty instead of trusting a decorative line.",
    width: 1920,
    height: 1080,
  },
  {
    section: "nikola",
    src: "/help/nikola-context.webp",
    alt: "Chessla Nikola and privacy settings with personal goals and coaching context",
    caption:
      "Settings · tell Nikola your goals and review privacy controls. Personal context is not verified chess evidence.",
    width: 1920,
    height: 1080,
  },
] as const;

const BASIS: Record<PrincipleId, { phase: string; basis: string }> = {
  center_claim: { phase: "Opening", basis: "Central occupation and control before wing drift." },
  pieces_out: { phase: "Opening", basis: "Safe minor-piece development from home squares." },
  tempo_discipline: { phase: "Opening", basis: "Repeated piece moves while development remains incomplete." },
  queen_patience: { phase: "Opening", basis: "Queen activity before minor-piece development or king safety." },
  pawn_economy: { phase: "Opening", basis: "Pawn moves must earn development, space, or a concrete break." },
  king_shelter: { phase: "Opening · Middlegame", basis: "Castling rights, king danger, and safe shelter geometry." },
  center_lock: {
    phase: "Opening · Middlegame",
    basis: "Central tension and the lines released by a pawn exchange or push.",
  },
  rook_harmony: { phase: "Middlegame", basis: "Completed development and unobstructed connection between rooks." },
  threat_read: { phase: "All phases", basis: "A concrete legal threat created by the opponent's last move." },
  force_first: { phase: "All phases", basis: "Verified checks, captures, or direct threats before quiet moves." },
  nothing_loose: { phase: "All phases", basis: "Attacked pieces without sufficient defense or tactical stability." },
  worst_piece: {
    phase: "Middlegame",
    basis: "Relative mobility and a verified improving move for the least-active piece.",
  },
  file_control: { phase: "Middlegame", basis: "Rook access to an open or half-open file." },
  outpost: { phase: "Middlegame", basis: "A supported square enemy pawns cannot challenge." },
  break_timing: { phase: "Middlegame", basis: "A prepared pawn break that changes a locked structure." },
  pawn_integrity: {
    phase: "All phases",
    basis: "New isolated, doubled, or lasting pawn weakness without compensation.",
  },
  clean_conversion: {
    phase: "Middlegame · Endgame",
    basis: "A material edge and a verified way to reduce counterplay.",
  },
  active_king: { phase: "Endgame", basis: "Low major-piece danger and safe king centralization." },
  opposition: { phase: "Endgame", basis: "Exact king-and-pawn opposition or key-square geometry." },
  passer_support: { phase: "Endgame", basis: "Passed-pawn creation, blockade, advance, and support." },
};

export const HELP_PRINCIPLES = PRINCIPLE_IDS.map((id) => ({ id, ...BASIS[id] }));

const citedPassage = (url: string, text: string) => `${url}#:~:text=${encodeURIComponent(text)}`;

export const HELP_RELATIONSHIPS = {
  pin: {
    description:
      "A bishop, rook, or queen attacks a piece shielding its king. That piece cannot legally leave the line if doing so exposes check. It may still move along the line or capture the attacker when legal.",
    source: citedPassage("https://www.chess.com/terms/pin-chess", "What Is An Absolute Pin?"),
  },
  relative_pin: {
    description:
      "A piece stands between an attacker and a more valuable friendly piece. Moving away may expose that piece to capture, but can still be legal and sometimes useful. The alignment alone does not prove a loss.",
    source: citedPassage("https://www.chess.com/terms/pin-chess", "What Is A Relative Pin?"),
  },
  skewer: {
    description:
      "A bishop, rook, or queen attacks the more valuable of two enemy pieces on one line. If the front piece moves away, the piece behind may become exposed. Chessla marks the alignment, not a guaranteed winning capture.",
    source: citedPassage("https://www.chess.com/terms/skewer-chess", "What Is A Skewer In Chess?"),
  },
  fork: {
    description:
      "One piece attacks several enemy pieces at once. Chessla marks forks involving at least two targets other than pawns. The opponent may have a defense, so this badge alone does not establish a material gain.",
    source: citedPassage("https://www.chess.com/terms/fork-chess", "What Is A Fork In Chess?"),
  },
  hanging: {
    description:
      "An attacked piece has no friendly piece guarding its square. This is an undefended target to examine, not a command to capture: checks, pins, and the opponent's reply can change the result. A safe capture is not guaranteed.",
    source: citedPassage("https://www.chess.com/terms/hanging-piece-chess", "What Is A Hanging Piece?"),
  },
  support: {
    description:
      "A friendly piece guards another piece's square. This shows their defensive connection, but a pinned defender may be unable to recapture legally. Select a piece to inspect its support, or enable All verified relationships.",
    source: citedPassage("https://www.chess.com/terms/chess-defense", "Black protects their bishop with a knight."),
  },
  xray: {
    description:
      "A bishop, rook, or queen lines up with an enemy target through one enemy piece. That blocker still interrupts the attack. Chessla uses the more specific pin or skewer badge when applicable; an x-ray alone proves no winning tactic.",
    source: citedPassage(
      "https://www.chess.com/article/view/the-most-important-tactical-pattern",
      "So, I described chess X-rays",
    ),
  },
  discovered_potential: {
    description:
      "A friendly piece blocks a bishop, rook, or queen, and has a legal move that uncovers an attack on an enemy piece. The badge describes an available possibility in this position. Potential does not prove a played tactic or a material gain.",
    source: citedPassage(
      "https://www.chess.com/terms/discovered-attack-chess",
      "What Is A Discovered Attack In Chess?",
    ),
  },
  check: {
    description:
      "The king of the side to move is under attack. Its next legal move must remove every check. Chessla draws an arrow from each checking piece; the check color takes priority when that king is also part of a fork.",
    source: citedPassage("https://www.chess.com/terms/check-chess", "What Is Check?"),
  },
} satisfies Record<keyof typeof POSITION_RELATIONSHIP_LEGEND, { description: string; source: string }>;

export const HELP_PRINCIPLE_SOURCE_BY_ID: Record<PrincipleId, string> = {
  center_claim: citedPassage("https://www.chess.com/article/view/learn-chess-opening-principles", "Control The Center"),
  pieces_out: citedPassage(
    "https://www.chess.com/article/view/the-principles-of-the-opening",
    "Rule #1 - Develop Your Pieces!",
  ),
  tempo_discipline: citedPassage(
    "https://www.chess.com/article/view/the-principles-of-the-opening",
    "Rule #4 - Don't Move a Piece Twice Before Move 10!",
  ),
  queen_patience: citedPassage(
    "https://www.chess.com/article/view/basic-opening-principles-part-1",
    "The main idea of opening is to get the pieces",
  ),
  pawn_economy: citedPassage(
    "https://www.chess.com/article/view/the-principles-of-the-opening",
    'Rule #10 - Attack "In the Direction" of Your Pawn(s) Structure!',
  ),
  king_shelter: citedPassage("https://www.chess.com/article/view/learn-chess-opening-principles", "Protect Your King"),
  center_lock: citedPassage("https://www.chess.com/terms/pawn-structure", "What Are Pawn Structures?"),
  rook_harmony: citedPassage(
    "https://www.chess.com/article/view/the-principles-of-the-opening",
    "Rule #8 - Connect the Rooks!",
  ),
  threat_read: citedPassage(
    "https://www.chess.com/article/view/spotting-and-avoiding-tactics",
    "Every time you consider a candidate move",
  ),
  force_first: citedPassage(
    "https://www.chess.com/article/view/spotting-and-avoiding-tactics",
    "check, capture, or threat",
  ),
  nothing_loose: citedPassage(
    "https://www.chess.com/article/view/spotting-and-avoiding-tactics",
    "loose pieces drop off",
  ),
  worst_piece: citedPassage("https://www.chess.com/article/view/good-and-bad-pieces", "are all my pieces happy?"),
  file_control: citedPassage("https://www.chess.com/terms/chess-file", "Why Are Files Important?"),
  outpost: citedPassage("https://www.chess.com/terms/outpost-chess", "What Is An Outpost?"),
  break_timing: citedPassage(
    "https://www.chess.com/article/view/pawn-breaks-part-1",
    "how much difference can a simple pawn break make",
  ),
  pawn_integrity: citedPassage("https://www.chess.com/terms/pawn-structure", "What Are Pawn Structure Weaknesses?"),
  clean_conversion: citedPassage(
    "https://www.chess.com/article/view/how-to-finish-a-won-game",
    "avoid a queen endgame by trading queens",
  ),
  active_king: citedPassage(
    "https://www.chess.com/terms/chess-endgame",
    "The king becomes a powerful piece in the endgame.",
  ),
  opposition: citedPassage("https://www.chess.com/terms/opposition-chess", "What Is Opposition?"),
  passer_support: citedPassage("https://www.chess.com/terms/passed-pawn", "What Is A Passed Pawn?"),
};

export const HELP_GLOSSARY_GROUPS = [
  {
    id: "pattern-icons",
    title: "Pattern and tactical board icons",
    entries: (Object.keys(PATTERN_GLYPHS) as PatternGlyphKind[]).map((motif) => ({
      id: `pattern-${motif}`,
      label: PATTERN_GLYPHS[motif].label,
      definition: PATTERN_GLYPHS[motif].definition,
      visual: { kind: "motif" as const, motif, mark: "", color: "#f0ece2" },
    })),
  },
  {
    id: "moves",
    title: "Move evidence",
    entries: [
      {
        id: "excellent-good",
        label: "Excellent / Good",
        definition:
          "Move-quality grades shown with a check mark. This is a quality assessment, not a check against the king.",
        visual: { kind: "motif", motif: "check", mark: "✓", color: "#5f9f45" },
      },
      {
        id: "check",
        label: "Check",
        definition:
          "The king is attacked and the next move must resolve that check. A structural check badge does not claim the checking move is best.",
        visual: { kind: "motif", motif: "text", mark: "+", color: "#f0ece2" },
      },
      {
        id: "checkmate",
        label: "Checkmate",
        definition:
          "The king is in check with no legal way to escape. This outcome is distinct from a mating threat or mating net.",
        visual: { kind: "motif", motif: "text", mark: "#", color: "#f0ece2" },
      },
      {
        id: "resignation",
        label: "Won by resignation",
        definition:
          "The recorded game ended when the opponent resigned. The flag describes the result, not the quality of the final move.",
        visual: { kind: "motif", motif: "flag", mark: "", color: "#f0ece2" },
      },
      {
        id: "clock",
        label: "Clock evidence",
        definition:
          "Clock context or a win on time. Read the badge label to distinguish time pressure from a recorded time-forfeit result.",
        visual: { kind: "motif", motif: "clock", mark: "", color: "#f59e0b" },
      },
      {
        id: "brilliant",
        label: "Brilliant",
        definition: "A rare move that keeps verified strength despite a surprising concession.",
        visual: { kind: "motif", motif: "brilliant", mark: "!!", color: "#22b8cf" },
      },
      {
        id: "best",
        label: "Best",
        definition: "The strongest verified choice in the analyzed position.",
        visual: { kind: "motif", motif: "best", mark: "BEST", color: "#81b64c" },
      },
      {
        id: "book",
        label: "Book",
        definition: "A move still inside recognized opening play.",
        visual: { kind: "motif", motif: "book", mark: "BOOK", color: "#c9a869" },
      },
      {
        id: "inaccuracy",
        label: "Inaccuracy",
        definition: "A modest loss that leaves the game playable.",
        visual: { kind: "motif", motif: "text", mark: "?!", color: "#eab308" },
      },
      {
        id: "mistake",
        label: "Mistake",
        definition: "A consequential loss that changes the practical task.",
        visual: { kind: "motif", motif: "text", mark: "?", color: "#f97316" },
      },
      {
        id: "blunder",
        label: "Blunder",
        definition: "A severe verified loss, often tactical or decisive.",
        visual: { kind: "motif", motif: "text", mark: "??", color: "#ff3d00" },
      },
    ],
  },
  {
    id: "review",
    title: "Review and coaching",
    entries: [
      {
        id: "critical-moment",
        label: "Critical moment",
        definition: "A teachable decision selected from your verified game evidence.",
        visual: { kind: "review", review: "critical-moment" },
      },
      {
        id: "principle",
        label: "Principle",
        definition: "A deterministic chess habit marked as applied or missed in the exact position.",
        visual: { kind: "principle", principle: "threat_read" },
      },
      {
        id: "pattern",
        label: "Pattern",
        definition: "The same verified decision problem recurring in one game format.",
        visual: { kind: "review", review: "pattern" },
      },
      {
        id: "focus",
        label: "Focus",
        definition: "One accepted practice target measured against later games.",
        visual: { kind: "review", review: "focus" },
      },
      {
        id: "verified-line",
        label: "Verified line",
        definition: "The shortest legal continuation needed to establish a consequence.",
        visual: { kind: "review", review: "verified-line" },
      },
    ],
  },
  {
    id: "position",
    title: "Position and numbers",
    entries: [
      {
        id: "evaluation",
        label: "Evaluation",
        definition: "Position value from your perspective. Positive favors you; negative favors the opponent.",
        visual: { kind: "notation", mark: "+1.2" },
      },
      {
        id: "mate",
        label: "Mate · M3",
        definition: "A forced checkmate. The number counts moves until mate in the verified line.",
        visual: { kind: "notation", mark: "M3" },
      },
      {
        id: "accuracy",
        label: "Accuracy",
        definition: "How closely played moves preserved the position's verified value.",
        visual: { kind: "notation", mark: "94%" },
      },
      {
        id: "ply",
        label: "Ply",
        definition: "One move by one player. A full move contains two plies.",
        visual: { kind: "notation", mark: "PLY" },
      },
      {
        id: "fen",
        label: "FEN",
        definition: "The complete position code used to return to the exact board state.",
        visual: { kind: "notation", mark: "8×8" },
      },
    ],
  },
  {
    id: "formats",
    title: "Game formats",
    entries: [
      {
        id: "rapid",
        label: "Rapid",
        definition: "A longer live game. Evidence stays separate from faster formats.",
        visual: { kind: "format", format: "rapid" },
      },
      {
        id: "blitz",
        label: "Blitz",
        definition: "A fast live game with a distinct decision and clock profile.",
        visual: { kind: "format", format: "blitz" },
      },
      {
        id: "bullet",
        label: "Bullet",
        definition: "An ultra-fast live game where clock pressure dominates more decisions.",
        visual: { kind: "format", format: "bullet" },
      },
      {
        id: "daily",
        label: "Daily",
        definition: "A correspondence game measured separately from live chess.",
        visual: { kind: "format", format: "daily" },
      },
    ],
  },
] as const;

export const HELP_PRINCIPLE_SOURCES = [
  { label: "FIDE LAWS OF CHESS", href: "https://handbook.fide.com/chapter/E012023" },
  { label: "LICHESS PRACTICE", href: "https://lichess.org/practice" },
  { label: "CHESS.COM OPENING PRINCIPLES", href: "https://www.chess.com/article/view/the-principles-of-the-opening" },
  { label: "CHESS.COM PASSED PAWNS", href: "https://www.chess.com/terms/passed-pawn" },
  { label: "CHESS.COM OPPOSITION", href: "https://www.chess.com/terms/opposition-chess" },
] as const;
